import {
    Button,
    DateRangeField,
    Form,
    FormFooter,
    FormSection,
    Page,
    Spinner,
    DataTable,
    DataTableCell,
    DataTableRow,
    toast,
    Stack,
    Link,
    PaginationControls,
    IconPhone,
    IconUserLight,
    SelectField,
    Inline,
    IconTimes,
    IconDownload
} from '@7shifts/sous-chef';
import { useFormik } from 'formik';
import { __ } from 'i18n/translate';
import subDays from 'date-fns/subDays';
import { useState } from 'react';
import Chart from 'react-apexcharts';
import {
    getTemplateErrorReportList,
    getTemplateErrorReportPlot,
    getTemplateErrorReportCsv
} from 'api/reportsApi';
import { dateYearMonthDayHourMinuteSecond } from 'utils/date';
import { format } from 'date-fns';
import { truncateText } from 'utils/string';
import {
    ReportTemplateMessages,
    ReportTemplateChart,
    ReportListItem,
    FormData
} from 'types';
import { DataTableCustomComponent } from '@7shifts/sous-chef/dist/lists/DataTable/types';

type ReportData = {
    list: ReportListItem[];
    chart: {
        options: ApexCharts.ApexOptions;
        series: number[];
    };
    count: number;
    hasMore: boolean;
};

const TemplateErrorReportPage = () => {
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);

    const handleFilter = (statusError: string | null) => {
        const dateFrom = dateYearMonthDayHourMinuteSecond(
            formik.values.date.start
        );
        const dateTo = dateYearMonthDayHourMinuteSecond(formik.values.date.end);
        setPage(1);
        setStatusFilter(statusError);
        setLoading(true);
        getTemplateErrorReportList(dateFrom, dateTo, statusError, 1)
            .then((listData: ReportTemplateMessages) => {
                setReportData((prevData) => {
                    if (!prevData) return null;
                    return {
                        ...prevData,
                        list: listData.messages,
                        count: listData.count,
                        hasMore: listData.hasMore
                    };
                });
            })
            .catch(() => {
                toast(__('reportsPage.reportTemplateGeneratedError'), 'danger');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const formik = useFormik<FormData>({
        initialValues: {
            date: {
                start: subDays(new Date(), 1),
                end: new Date()
            }
        },
        onSubmit: (values, { setSubmitting }) => {
            setReportData(null);
            setPage(1);
            setStatusFilter('');
            setLoading(true);
            setSubmitting(true);

            const dateFrom = dateYearMonthDayHourMinuteSecond(
                values.date.start
            );
            const dateTo = dateYearMonthDayHourMinuteSecond(values.date.end);

            Promise.all([
                getTemplateErrorReportList(dateFrom, dateTo, '', 1),
                getTemplateErrorReportPlot(dateFrom, dateTo)
            ])
                .then(
                    ([listData, chartData]: [
                        ReportTemplateMessages,
                        ReportTemplateChart[]
                    ]) => {
                        const chartDataItem = chartData.reduce(
                            (
                                acc: { series: number[]; labels: string[] },
                                item: ReportTemplateChart
                            ) => {
                                acc.series.push(Number(item.total));
                                acc.labels.push(item.status);
                                return acc;
                            },
                            { series: [], labels: [] }
                        );

                        const handleLegendClick = (
                            chartContext: ApexCharts.ApexOptions,
                            seriesIndex: number
                        ) => {
                            console.log(chartContext);
                            const statusError =
                                chartDataItem.labels[seriesIndex];

                            handleFilter(statusError);
                        };

                        setReportData({
                            list: listData.messages,
                            chart: {
                                series: chartDataItem.series,
                                options: {
                                    chart: {
                                        height: 350,
                                        type: 'pie',
                                        events: {
                                            legendClick: handleLegendClick
                                        }
                                    },
                                    labels: chartDataItem.labels,
                                    legend: {
                                        position: 'right',
                                        horizontalAlign: 'center',
                                        floating: false,
                                        width: 450
                                    }
                                }
                            },
                            count: listData.count,
                            hasMore: listData.hasMore
                        });
                        toast(__('reportsPage.reportTemplateGeneratedSuccess'));
                    }
                )
                .catch(() => {
                    toast(
                        __('reportsPage.reportTemplateGeneratedError'),
                        'danger'
                    );
                })
                .finally(() => {
                    setLoading(false);
                    formik.setSubmitting(false);
                });
        }
    });

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setLoading(true);
        const dateFrom = dateYearMonthDayHourMinuteSecond(
            formik.values.date.start
        );
        const dateTo = dateYearMonthDayHourMinuteSecond(formik.values.date.end);

        getTemplateErrorReportList(dateFrom, dateTo, statusFilter, newPage)
            .then((listData: ReportTemplateMessages) => {
                setReportData((prevData) => {
                    if (!prevData) return null;
                    return {
                        ...prevData,
                        list: listData.messages,
                        count: listData.count,
                        hasMore: listData.hasMore
                    };
                });
            })
            .catch(() => {
                toast(__('reportsPage.reportTemplateGeneratedError'), 'danger');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const [exporting, setExporting] = useState(false);

    const handleExportCsv = () => {
        const dateFrom = dateYearMonthDayHourMinuteSecond(
            formik.values.date.start
        );
        const dateTo = dateYearMonthDayHourMinuteSecond(formik.values.date.end);

        setExporting(true);
        getTemplateErrorReportCsv(dateFrom, dateTo, statusFilter)
            .then((csvContent) => {
                const blob = new Blob(['\ufeff' + csvContent], {
                    type: 'text/csv;charset=utf-8;'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute(
                    'download',
                    `relatorio-erros-template-${format(
                        new Date(),
                        'dd-MM-yyyy-HH-mm'
                    )}.csv`
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                toast(__('reportsPage.reportTemplateGeneratedError'), 'danger');
            })
            .finally(() => {
                setExporting(false);
            });
    };

    return (
        <Page
            breadcrumbs={__('main.reports')}
            title={__('reportsPage.reportTemplateError')}
            subtitle={__('reportsPage.reportTemplateErrorDescription')}
        >
            <Form formik={formik}>
                <FormSection as="card">
                    <DateRangeField
                        name="date"
                        label={__(
                            'reportsPage.reportTemplateListRangeFieldLabel'
                        )}
                        format="dd/MM/yyyy"
                        disabledDays={{
                            after: new Date()
                        }}
                    />
                </FormSection>
                <FormFooter
                    actions={{
                        primary: (
                            <Button type="submit" loading={formik.isSubmitting}>
                                {__('reportsPage.reportTemplateGenerateButton')}
                            </Button>
                        )
                    }}
                />
            </Form>

            {loading && !reportData && <Spinner block />}

            {reportData && (
                <FormSection as="card">
                    <Stack>
                        {reportData.chart.series.length > 0 && (
                            <Chart
                                options={reportData.chart.options}
                                series={reportData.chart.series}
                                type="pie"
                                height={350}
                            />
                        )}
                        <Stack>
                            <Inline
                                alignItems="flex-end"
                                justifyContent="space-between"
                            >
                                <div style={{ width: 300 }}>
                                    <SelectField
                                        label="Filtrar"
                                        placeholder={__(
                                            'component.select.placeholder'
                                        )}
                                        name="statusFilter"
                                        value={{
                                            label: statusFilter
                                                ? statusFilter
                                                : __(
                                                      'component.select.placeholder'
                                                  ),
                                            value: statusFilter
                                        }}
                                        onChange={({ value }) =>
                                            handleFilter(value)
                                        }
                                        options={[
                                            ...(
                                                reportData.chart.options
                                                    .labels || []
                                            )
                                                .map((label: string) => ({
                                                    label,
                                                    value: label
                                                }))
                                                .sort((a, b) =>
                                                    a.label.localeCompare(
                                                        b.label
                                                    )
                                                )
                                        ]}
                                    />
                                </div>
                                <Inline space={16}>
                                    {statusFilter && (
                                        <Button
                                            onClick={() => handleFilter('')}
                                            theme="link-danger"
                                        >
                                            <IconTimes size="small" />
                                            Limpar filtro
                                        </Button>
                                    )}
                                    <Button
                                        onClick={handleExportCsv}
                                        theme="default"
                                        loading={exporting}
                                    >
                                        <IconDownload size="small" />
                                        Exportar CSV
                                    </Button>
                                </Inline>
                            </Inline>
                            <DataTable
                                items={reportData.list}
                                isLoading={loading}
                                itemComponent={ReportRow}
                                maxHeight={500}
                                columns={[
                                    {
                                        name: 'ticketId',
                                        label: __(
                                            'reportsPage.reportTemplateListTicketId'
                                        )
                                    },
                                    {
                                        name: 'sendDate',
                                        label: __(
                                            'reportsPage.reportTemplateListSendDate'
                                        )
                                    },
                                    {
                                        name: 'contact',
                                        label: __(
                                            'reportsPage.reportTemplateListContactColumn'
                                        )
                                    },
                                    {
                                        name: 'content',
                                        label: __(
                                            'reportsPage.reportTemplateListContent'
                                        )
                                    }
                                ]}
                            />
                            <PaginationControls
                                hasNext={reportData.hasMore}
                                hasPrevious={page > 1}
                                onNextClick={() => handlePageChange(page + 1)}
                                onPreviousClick={() =>
                                    handlePageChange(page - 1)
                                }
                            />
                        </Stack>
                    </Stack>
                </FormSection>
            )}
        </Page>
    );
};

const ReportRow = ({ item }: DataTableCustomComponent<ReportListItem>) => {
    return (
        <DataTableRow>
            <DataTableCell columnIndex={0}>
                <Link
                    href={`/tickets?ticket=${item.ticket.uuid}`}
                    target="_blank"
                >
                    {item.ticketId}
                </Link>
            </DataTableCell>
            <DataTableCell columnIndex={1}>
                {format(new Date(item.createdAt), 'dd/MM/yyyy HH:mm:ss')}
            </DataTableCell>
            <DataTableCell columnIndex={2}>
                <Stack space={4}>
                    <Inline space={8}>
                        <IconUserLight size="small" />
                        {item.ticket.contact.name}
                    </Inline>
                    <Inline space={8}>
                        <IconPhone size="small" />
                        {item.ticket.contact.number}
                    </Inline>
                </Stack>
            </DataTableCell>
            <DataTableCell columnIndex={3}>
                {truncateText(item.body, 50)}
            </DataTableCell>
        </DataTableRow>
    );
};

export default TemplateErrorReportPage;
